var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EVENT_NAME_MESSAGE, messageVersion, SatelliteModes, IFRAME_LOAD_EVENT, } from './Constants';
import { MessageType, MetricType, } from './Interfaces';
import { constructSerializableSatelliteProps, generateNewMessageId, resolveOrigin, validateMessage, validMessageType, } from './SatelliteUtil';
import { v4 as uuid } from 'uuid';
import { DependencyError, NetworkError, TimeoutEventError, } from '@amzn/csc-ui-module-commons';
/**
 * Number of times we attempt to make the handshake successful.
 */
export const maxRetryCount = 6;
/**
 * Maximum time delay between consecutive retries.
 */
export const maxRetryTimeInMillis = 250;
/**
 * Satellite library enables an application to embed another application that is hosted on a separate environment through the use of Iframes or Service Worker Client.
 * It provides useful abstraction over Iframe & Service Worker Client and handles the complexities such as message passing, dealing with promises etc from the parent application.
 * Usage of this library:
 *      new Satellite({
 *         mode: '<Iframe or ServiceWorkerClient>',
 *         url: '<Url of the embedding application>',
 *         id: 'divId',
 *         props: <key-value pairs & functions that needs to be exposed to the embedding application>,
 *      }).load();
 */
export class Satellite {
    constructor({ mode, url, id, props, resizeCallback, titleChangeCallback, recordPerformanceMetricsCallback, isPrefetch = false, selector, handshakeTimeoutInMillis, serviceWorkerClient, }) {
        this.mode = mode;
        this.url = url;
        this.id = id;
        this.props = props;
        this.isPrefetch = isPrefetch;
        this.resizeCallback = resizeCallback;
        this.titleChangeCallback = titleChangeCallback;
        this.recordPerformanceMetricsCallback = recordPerformanceMetricsCallback;
        this.handshakeTimeoutInMillis = handshakeTimeoutInMillis;
        this.embedder = this.getEmbedder(selector, id);
        this.serviceWorkerClient = this.getServiceWorkerClient(serviceWorkerClient);
        this.services = this.getServicesFromProps(props);
        this.serializableProps = constructSerializableSatelliteProps(props);
        this.satelliteClientOrigin = resolveOrigin(url);
        this.performHandshake = this.performHandshake.bind(this);
        this.emitListener = this.emitListener.bind(this);
        this.requestResponseListener = this.requestResponseListener.bind(this);
        this.debugListener = this.debugListener.bind(this);
        this.resizeListener = this.resizeListener.bind(this);
        this.titleChangeListener = this.titleChangeListener.bind(this);
        this.eventRouter = this.eventRouter.bind(this);
        this.recordPostMessageMetrics = this.recordPostMessageMetrics.bind(this);
        this.handshakeComplete = this.handshakeComplete.bind(this);
        this.load = this.load.bind(this);
        this.postSatelliteEventPayloadToClient = this.postSatelliteEventPayloadToClient.bind(this);
        this.subscribeToSatelliteEvents = this.subscribeToSatelliteEvents.bind(this);
        this.removeMessageListeners = this.removeMessageListeners.bind(this);
    }
    /**
     * This method is responsible for embedding the guest application from the specified url into the parent application.
     * It performs the handshake on load of the guest application & adds necessary message listeners.
     */
    load() {
        var _a;
        this.satelliteToken = uuid(); // new token everytime, in case load is called again
        this.addMessageListeners(globalThis);
        if (this.mode === SatelliteModes.IFRAME) {
            (_a = this.embedder) === null || _a === void 0 ? void 0 : _a.addEventListener(IFRAME_LOAD_EVENT, this.performHandshake);
            this.embedder.style.display = 'block';
            this.embedder.src = this.url || '';
        }
        else {
            // If in service worker, perform handshake immediately since by the time Satellite is initialized,
            // the client pages should be ready for handshake.
            this.performHandshake();
        }
        this.handshakePromise = new Promise((resolve, reject) => {
            this.resolveHandshake = resolve;
            this.rejectHandshake = reject;
        });
        this.handshakePromise.then(() => {
            // Once the handshake is completed both ends, subscribe to the events.
            // This is done so that if there are any buffered events they can safely be dispatched to the iframes
            // which should be listening by now
            this.subscribeToSatelliteEvents(this.props);
        }, (x) => {
            // Warn the satellite subscribing issue if any.
            // HandshakeComplete will be rejected by function rejectHandshake.
            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/then#onrejected
            console.warn('Unable to subscribe to satellite: ' + x.message);
        });
        if (this.handshakeTimeoutInMillis) {
            // per https://stackoverflow.com/questions/20328073/is-it-safe-to-resolve-a-promise-multiple-times
            // any other calls to resolve or reject will have no effect
            this.handshakeTimerId = globalThis.setTimeout(() => {
                const error = new TimeoutEventError('satelliteLoadRemoteComponent', `Handshake timed out after ${this.handshakeTimeoutInMillis} ms trying to load ${this.id} from url ${this.url}`);
                this.rejectHandshake(error);
            }, this.handshakeTimeoutInMillis);
        }
        return this;
    }
    /**
     * This method is responsible for removing the guest application from the parent application.
     * It removes all event listeners that have been added.
     */
    unload() {
        if (this.handshakeTimerId) {
            globalThis.clearTimeout(this.handshakeTimerId);
        }
        this.removeMessageListeners(globalThis);
        if (this.clientPort) {
            this.removeMessageListeners(this.clientPort);
            this.clientPort.close();
        }
        if (this.mode === SatelliteModes.IFRAME) {
            this.embedder.removeEventListener(IFRAME_LOAD_EVENT, this.performHandshake);
            this.embedder.style.display = 'none';
            this.embedder.src = 'about:blank';
        }
        return this;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handshakeComplete() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.handshakePromise;
        });
    }
    /**
     * private utility for gracefully retrieving a field called 'services' from given object.
     * @param props an object that may be undefined.
     */
    getServicesFromProps(props) {
        return (props || {}).services || {};
    }
    updateServices(services) {
        this.services = services;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subscribeToSatelliteEvents(props) {
        if (!props.events)
            return {};
        Object.keys(props.events).forEach((eventName) => {
            const eventBus = props.events[eventName];
            const handler = (payload) => {
                this.postSatelliteEventPayloadToClient(eventName, payload);
            };
            eventBus.subscribe(handler);
        });
    }
    /**
     *
     * Formats and posts a Satellite Event payload
     *
     * @internal to be used by SatelliteEventBus
     * @param satelliteEventName
     * @param payload
     */
    postSatelliteEventPayloadToClient(satelliteEventName, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload) {
        const message = {
            type: MessageType.USER_EVENT_PAYLOAD,
            messageVersion,
            uid: generateNewMessageId(),
            satelliteToken: this.satelliteToken,
            eventName: satelliteEventName,
            payload: payload,
        };
        this.clientPort.postMessage(message);
    }
    /**
     * Once the guest application is loaded, we perform a one-time handshake between the parent & the guest application.
     * Purpose of the handshake is to let the guest application know about the parent application. This is the only way by which,
     * the guest application gets the reference of the parent window object for future communication. As part of the handshake, we
     * also expose certain properties/functionalities that the parent application wanted to.
     * @param e
     */
    performHandshake(e) {
        const handshakeTarget = this.mode === SatelliteModes.IFRAME
            ? e.target.contentWindow
            : this.serviceWorkerClient;
        let retryInterval;
        let attempt = 0;
        let handshakeSuccessful = false;
        const uid = generateNewMessageId();
        const succeedHandshake = (response) => {
            var _a, _b;
            if (this.handshakeTimerId) {
                globalThis.clearTimeout(this.handshakeTimerId);
            }
            if (response.remoteCardMetrics &&
                this.recordPerformanceMetricsCallback) {
                this.recordPerformanceMetricsCallback(response.remoteCardMetrics.hostName + '.RemoteCardSizeInMB', response.remoteCardMetrics.remoteCardSize, MetricType.COUNTER, this.id);
                this.recordPerformanceMetricsCallback(response.remoteCardMetrics.hostName + '.DownloadTime', response.remoteCardMetrics.remoteCardDownloadTime, MetricType.TIMER, this.id);
            }
            if ((_a = response === null || response === void 0 ? void 0 : response.options) === null || _a === void 0 ? void 0 : _a.isFinalHandshake) {
                (_b = this.embedder) === null || _b === void 0 ? void 0 : _b.removeEventListener(IFRAME_LOAD_EVENT, this.performHandshake);
            }
            console.debug('Handshake Successful: ', response);
            handshakeSuccessful = true;
            this.resolveHandshake(response); // Expose HandshakeReply to parent application
        };
        const failHandshake = (error) => {
            console.error('Handshake Failed: ', error);
            if (this.handshakeTimerId) {
                globalThis.clearTimeout(this.handshakeTimerId);
            }
            this.rejectHandshake(error);
        };
        const handleHandshakeReply = (e) => {
            const messageValidation = validateMessage(e, this.satelliteClientOrigin, this.satelliteToken);
            if (messageValidation === validMessageType.INVALID) {
                failHandshake(new NetworkError('No valid message', e));
                return;
            }
            if (messageValidation === validMessageType.INCORRECT_TOKEN ||
                messageValidation === validMessageType.IGNORE) {
                return;
            }
            const handshakeResponse = e.data;
            if (handshakeResponse.uid === uid &&
                handshakeResponse.type === MessageType.HANDSHAKE_REPLY) {
                this.recordPostMessageMetrics(handshakeResponse);
                clearInterval(retryInterval);
                this.clientPort = e.currentTarget;
                this.clientPort.removeEventListener(EVENT_NAME_MESSAGE, handleHandshakeReply);
                succeedHandshake(handshakeResponse);
                return;
            }
            else {
                failHandshake(new DependencyError('No valid handshakeResponse', handshakeResponse));
                return;
            }
        };
        const sendHandshakeMessage = () => {
            attempt++;
            const serializableProps = this
                .serializableProps;
            const handshakeMessage = {
                type: MessageType.HANDSHAKE,
                messageVersion,
                props: serializableProps,
                uid,
                satelliteToken: this.satelliteToken,
                isPrefetch: this.isPrefetch,
            };
            // Send the message only if the page was loaded from the Satellite client origin.
            const mc = new MessageChannel();
            const clientPort = mc.port1;
            if (this.mode === SatelliteModes.IFRAME) {
                handshakeTarget.postMessage(handshakeMessage, this.satelliteClientOrigin, [mc.port2]);
            }
            else {
                handshakeTarget.postMessage(handshakeMessage, [mc.port2]);
            }
            // Prepare for ACK from Child...
            clientPort.addEventListener(EVENT_NAME_MESSAGE, handleHandshakeReply);
            this.addMessageListeners(clientPort);
            clientPort.start();
            // check the handshake status, and close unused ports
            // if handshake is not successful, just close all ports
            setTimeout(() => {
                if (!handshakeSuccessful || this.clientPort != clientPort) {
                    clientPort.removeEventListener(EVENT_NAME_MESSAGE, handleHandshakeReply);
                    this.removeMessageListeners(clientPort);
                    clientPort.close();
                }
            }, maxRetryTimeInMillis * maxRetryCount);
            if (attempt === maxRetryCount) {
                clearInterval(retryInterval);
            }
        };
        const beginHandshake = () => {
            sendHandshakeMessage();
            retryInterval = setInterval(sendHandshakeMessage, maxRetryTimeInMillis);
        };
        beginHandshake();
    }
    /**
     * This is a message listener, responsible for listening to messages of type 'emit'
     * An 'emit' message is sent by the guest application for performing certain exposed functionality such as services for which
     * the guest application doesn't care about the response.
     * @param e
     */
    emitListener(e) {
        const message = e.data;
        const property = message.property;
        this.recordPostMessageMetrics(message);
        if (property in this.services) {
            const callback = this.services[property];
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            callback(...message.inputsArray);
        }
    }
    /**
     * This is a message listener, responsible for listening to messages of type 'request'
     * A 'request' message is sent by the client application for performing certain exposed functionality such as services for which
     * the guest application is expecting to receive a response back.
     * @param e
     */
    requestResponseListener(e) {
        const message = e.data;
        this.recordPostMessageMetrics(message);
        const property = message.property;
        if (property in this.services) {
            const callback = this.services[property];
            const inputsArray = message.inputsArray;
            const response = callback(...inputsArray);
            const uid = message.uid;
            Promise.resolve(response)
                .then((response) => {
                // Then send the response back to client port.
                const message = {
                    type: MessageType.REPLY,
                    messageVersion,
                    property,
                    response,
                    uid,
                    satelliteToken: this.satelliteToken,
                };
                this.postMessage(e, message);
            })
                .catch((error) => {
                const message = {
                    type: MessageType.REPLY_EXCEPTION,
                    messageVersion,
                    property,
                    error,
                    uid,
                    satelliteToken: this.satelliteToken,
                };
                this.postMessage(e, message);
            });
        }
    }
    postMessage(e, message) {
        this.clientPort.postMessage(message);
    }
    /**
     * This is a message listener responsible for resizing embedder content.
     * @param e
     */
    resizeListener(e) {
        const message = e.data;
        if (this.resizeCallback) {
            this.resizeCallback(message.height);
        }
    }
    /**
     * This is a message listener responsible for changing component title.
     * @param e
     */
    titleChangeListener(e) {
        const message = e.data;
        if (this.titleChangeCallback) {
            this.titleChangeCallback(message.title);
        }
    }
    recordPostMessageMetrics(message) {
        if (this.recordPerformanceMetricsCallback && message.timestamp) {
            const latency = Date.now() - message.timestamp;
            const type = MessageType[message.type];
            this.recordPerformanceMetricsCallback(type + '.Latency', latency, MetricType.TIMER, this.id);
        }
    }
    /**
     * This is a message listener responsible for logging all the messages received.
     * @param e
     */
    debugListener(e) {
        console.debug('[Satellite-Debug-Info] Message received: ', e.data);
    }
    /**
     * This is the message event listener which calls the event handler corresponding to the message type
     */
    eventRouter(e) {
        const message = e.data;
        if (validateMessage(e, this.satelliteClientOrigin, this.satelliteToken) !== validMessageType.VALID) {
            return;
        }
        switch (message.type) {
            case MessageType.EMIT:
                this.emitListener(e);
                break;
            case MessageType.REQUEST:
                this.requestResponseListener(e);
                break;
            case MessageType.RESIZE:
                this.resizeListener(e);
                break;
            case MessageType.TITLE_CHANGE:
                this.titleChangeListener(e);
                break;
        }
        this.debugListener(e);
    }
    /**
     * Utility for cleaning up all added message listeners. Useful while unloading the guest application.
     */
    removeMessageListeners(target) {
        target.removeEventListener(EVENT_NAME_MESSAGE, this.eventRouter);
    }
    /**
     * Utility for adding the different message listeners.
     */
    addMessageListeners(target) {
        target.addEventListener(EVENT_NAME_MESSAGE, this.eventRouter);
    }
    /**
     * Get embedder by selector or id, or return undefined if we are in service worker.
     * @param selector Selector string to select the iframe to be embedded in shell app
     * @param id Alternativly, we can use iframe id to select it
     * @returns Selected element or undefined
     */
    getEmbedder(selector, id) {
        let embedder = undefined;
        if (this.mode === SatelliteModes.IFRAME) {
            if (selector) {
                embedder = document.querySelector(selector);
            }
            else {
                embedder = document.querySelector(`[id='${id}']`);
            }
            // If we are in browser, embedder is required
            if (!embedder) {
                throw new DependencyError('Embedder is empty, cannot create satellite listener.');
            }
        }
        return embedder;
    }
    /**
     * Get service worker client if Satellite is running in service worker, otherwise return undefined.
     * @param serviceWorkerClient serviceWorkerClient passed in from props
     * @returns serviceWorkerClient or undefined
     */
    getServiceWorkerClient(serviceWorkerClient) {
        if (this.mode === SatelliteModes.SERVICE_WORKER_CLIENT &&
            !serviceWorkerClient) {
            throw new DependencyError('ServiceWorkerClient is required when in service worker, but empty.');
        }
        return serviceWorkerClient;
    }
}
//# sourceMappingURL=Satellite.js.map